import { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Progress,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useTicketOptions } from '@/hooks/useTicketOptions'
import { partIsLinkedToTask } from '@/lib/utils/ticketPartLinkage'
import type { TicketPart, TicketPartStatus } from '@/types/parentTicket'
import { PROCUREMENT_FINAL_STATUSES } from './taskUtils'

const STATUS_LABELS: Record<TicketPartStatus, string> = {
  pending: 'Pending',
  ordered: 'Ordered',
  available: 'Available',
  not_available: 'Not available',
  received: 'Received',
}

const STATUS_COLORS: Record<TicketPartStatus, string> = {
  pending: 'gray',
  ordered: 'blue',
  available: 'green',
  not_available: 'red',
  received: 'purple',
}

type ItemCardProps = {
  part: TicketPart
  ticketId: string
  isReadOnly: boolean
  onUpdated: () => void
}

function ProcurementItemCard({ part, ticketId, isReadOnly, onUpdated }: ItemCardProps) {
  const colors = useThemeColors()
  const { showError } = useToastNotification()
  const { options: whseOptions } = useTicketOptions('whse_availability')
  const [expanded, setExpanded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formPartCode, setFormPartCode] = useState(part.part_code ?? '')
  const [formStatus, setFormStatus] = useState<TicketPartStatus>(part.status ?? 'pending')
  const [formMrCreatedAt, setFormMrCreatedAt] = useState(part.mr_created_at ? part.mr_created_at.slice(0, 16) : '')
  const [formExpectedDays, setFormExpectedDays] = useState(
    part.expected_delivery_lead_time_days != null ? String(part.expected_delivery_lead_time_days) : '',
  )
  const [formWhse, setFormWhse] = useState<'available' | 'not_available' | ''>(part.whse_availability ?? '')
  const [formFeLeadDays, setFormFeLeadDays] = useState(
    part.fe_lead_time_days != null ? String(part.fe_lead_time_days) : '',
  )

  const savePart = useCallback(
    async (overrides?: Partial<{ status: TicketPartStatus }>) => {
      setSaving(true)
      const res = await TicketService.updateTicketPart(ticketId, part.id, {
        part_name: part.part_name,
        part_code: formPartCode.trim() || null,
        quantity: part.quantity ?? null,
        unit: part.unit ?? null,
        notes: part.notes ?? null,
        status: overrides?.status ?? formStatus,
        mr_created_at: formMrCreatedAt ? new Date(formMrCreatedAt).toISOString() : null,
        expected_delivery_lead_time_days: formExpectedDays !== '' ? parseInt(formExpectedDays, 10) : null,
        whse_availability: formWhse || null,
        fe_lead_time_days: formFeLeadDays !== '' ? parseInt(formFeLeadDays, 10) : null,
      })
      setSaving(false)
      if (res.success) {
        onUpdated()
      } else {
        showError(new Error(res.message ?? 'Failed to save part details'))
      }
    },
    [
      formExpectedDays,
      formFeLeadDays,
      formMrCreatedAt,
      formPartCode,
      formStatus,
      formWhse,
      onUpdated,
      part,
      showError,
      ticketId,
    ],
  )

  const status = part.status ?? 'pending'

  return (
    <Box border="1px solid" borderColor={colors.borderLight} borderRadius="md" p={3} mb={3}>
      <HStack justify="space-between" align="flex-start" mb={2}>
        <Box>
          <Text fontSize="sm" fontWeight="700">
            {part.part_name}
          </Text>
          <Text fontSize="xs" color={colors.textSecondary}>
            {part.quantity ?? '—'} {part.unit ?? ''}
          </Text>
        </Box>
        <Badge colorScheme={STATUS_COLORS[status]} variant="subtle" textTransform="none">
          {STATUS_LABELS[status]}
        </Badge>
      </HStack>

      {!isReadOnly ? (
        <Select
          size="sm"
          value={formStatus}
          mb={2}
          onChange={async (e) => {
            const next = e.target.value as TicketPartStatus
            setFormStatus(next)
            await savePart({ status: next })
          }}
          isDisabled={saving}
        >
          {(Object.keys(STATUS_LABELS) as TicketPartStatus[]).map((value) => (
            <option key={value} value={value}>
              {STATUS_LABELS[value]}
            </option>
          ))}
        </Select>
      ) : (
        <Text fontSize="sm" mb={2}>
          {STATUS_LABELS[status]}
        </Text>
      )}

      <Button
        variant="link"
        size="sm"
        colorScheme="blue"
        onClick={() => setExpanded((v) => !v)}
        mb={expanded ? 2 : 0}
      >
        Procurement details {expanded ? '▴' : '▾'}
      </Button>

      <Collapse in={expanded} animateOpacity>
        <VStack align="stretch" spacing={3} pt={2} borderTop="1px dashed" borderColor={colors.borderLight}>
          <FormControl>
            <FormLabel fontSize="xs">Part code</FormLabel>
            <Input
              size="sm"
              value={formPartCode}
              isDisabled={isReadOnly}
              onChange={(e) => setFormPartCode(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">MR created</FormLabel>
            <Input
              size="sm"
              type="datetime-local"
              value={formMrCreatedAt}
              isDisabled={isReadOnly}
              onChange={(e) => setFormMrCreatedAt(e.target.value)}
            />
          </FormControl>
          <HStack align="flex-start">
            <FormControl>
              <FormLabel fontSize="xs">Expected delivery (days)</FormLabel>
              <Input
                size="sm"
                type="number"
                min={0}
                value={formExpectedDays}
                isDisabled={isReadOnly}
                onChange={(e) => setFormExpectedDays(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">FE lead time (days)</FormLabel>
              <Input
                size="sm"
                type="number"
                min={0}
                value={formFeLeadDays}
                isDisabled={isReadOnly}
                onChange={(e) => setFormFeLeadDays(e.target.value)}
              />
            </FormControl>
          </HStack>
          <FormControl>
            <FormLabel fontSize="xs">WHSE availability</FormLabel>
            <Select
              size="sm"
              value={formWhse}
              isDisabled={isReadOnly}
              onChange={(e) => setFormWhse(e.target.value as typeof formWhse)}
            >
              <option value="">—</option>
              {whseOptions.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormControl>
          {!isReadOnly ? (
            <Button size="sm" colorScheme="blue" alignSelf="flex-start" isLoading={saving} onClick={() => void savePart()}>
              Save procurement details
            </Button>
          ) : null}
        </VStack>
      </Collapse>
    </Box>
  )
}

type Props = {
  ticketId: string
  childTicketId?: number | null
  ticketParts?: TicketPart[]
  isReadOnly?: boolean
  onSaved?: () => void
  /** When true, omit the embedded border-top spacing (e.g. drawer Parts tab). */
  standalone?: boolean
}

export default function PartProcurementPanel({
  ticketId,
  childTicketId,
  ticketParts = [],
  isReadOnly = false,
  onSaved,
  standalone = false,
}: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [markingAll, setMarkingAll] = useState(false)
  const [resolvedParts, setResolvedParts] = useState<TicketPart[]>(ticketParts)
  const [loadingParts, setLoadingParts] = useState(false)

  useEffect(() => {
    setResolvedParts(ticketParts)
  }, [ticketParts])

  useEffect(() => {
    if (!ticketId || childTicketId == null) return
    if (ticketParts.length > 0) return

    let cancelled = false
    setLoadingParts(true)
    void TicketService.getTicketParts(ticketId).then((res) => {
      if (cancelled) return
      setLoadingParts(false)
      if (res.success && res.data) {
        setResolvedParts(res.data)
      }
    })

    return () => {
      cancelled = true
    }
  }, [ticketId, childTicketId, ticketParts.length])

  const linkedParts = resolvedParts.filter((part) => partIsLinkedToTask(part, childTicketId))

  const receivedCount = linkedParts.filter((p) => (p.status ?? 'pending') === 'received').length
  const progressPct = linkedParts.length ? Math.round((receivedCount / linkedParts.length) * 100) : 0

  const handleMarkAllReceived = async () => {
    if (!linkedParts.length) return
    setMarkingAll(true)
    try {
      const results = await Promise.all(
        linkedParts.map((part) =>
          TicketService.updateTicketPart(ticketId, part.id, {
            part_name: part.part_name,
            part_code: part.part_code,
            quantity: part.quantity ?? null,
            unit: part.unit ?? null,
            notes: part.notes ?? null,
            status: 'received',
            mr_created_at: part.mr_created_at,
            expected_delivery_lead_time_days: part.expected_delivery_lead_time_days,
            whse_availability: part.whse_availability,
            fe_lead_time_days: part.fe_lead_time_days,
          }),
        ),
      )
      if (results.every((r) => r.success)) {
        showSuccess('All items marked received')
        onSaved?.()
      } else {
        showError(new Error('Some items could not be updated'))
        onSaved?.()
      }
    } finally {
      setMarkingAll(false)
    }
  }

  const containerProps = standalone
    ? {}
    : { mt: 4, pt: 4, borderTop: '1px solid', borderColor: colors.borderLight }

  if (!linkedParts.length) {
    return (
      <Box {...containerProps}>
        <Text fontSize="sm" color={colors.textSecondary}>
          {loadingParts ? 'Loading linked parts…' : 'No part is linked to this task.'}
        </Text>
      </Box>
    )
  }

  return (
    <Box {...containerProps}>
      <Text fontSize="sm" fontWeight="700" mb={2}>
        Part procurement ({linkedParts.length} item{linkedParts.length === 1 ? '' : 's'})
      </Text>
      <Progress value={progressPct} size="sm" colorScheme="green" borderRadius="full" mb={1} />
      <Text fontSize="xs" color={colors.textSecondary} mb={3}>
        {receivedCount} of {linkedParts.length} received
      </Text>

      {!isReadOnly ? (
        <Button size="sm" variant="outline" mb={3} isLoading={markingAll} onClick={() => void handleMarkAllReceived()}>
          Mark all received
        </Button>
      ) : null}

      {linkedParts.map((part) => (
        <ProcurementItemCard
          key={part.id}
          part={part}
          ticketId={ticketId}
          isReadOnly={isReadOnly}
          onUpdated={() => onSaved?.()}
        />
      ))}
    </Box>
  )
}

export { STATUS_LABELS as PROCUREMENT_STATUS_LABELS }
export { PROCUREMENT_FINAL_STATUSES } from './taskUtils'
